import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
// import brandsData from '../data/brands_data.json'; // Legacy source
import { 
  Users, 
  FileText, 
  Settings, 
  PlusCircle, 
  Search, 
  Filter,
  LogOut,
  ChevronRight,
  TrendingUp,
  ShieldCheck,
  Globe,
  Sparkles,
  RefreshCw,
  Database,
  Download,
  BookOpen,
  PieChart,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';

const API_URL = '/api.php';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [analyzing, setAnalyzing] = useState(null); 
  const [aiResults, setAiResults] = useState({});
  const [selectedAudit, setSelectedAudit] = useState(null); 
  const [entities, setEntities] = useState([]); 
  const [selectedClient, setSelectedClient] = useState(null); // Drill-level: Client
  const [selectedRound, setSelectedRound] = useState(null);   
  const [rounds, setRounds] = useState([]);
  const [logs, setLogs] = useState([]);
  const [activeView, setActiveView] = useState('overview'); 
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [settings, setSettings] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [brandsData, setBrandsData] = useState([]); // Dynamic source
  const [migrating, setMigrating] = useState(false);
  const [ipInfo, setIpInfo] = useState({ visitor_ip: 'Detecting...', server_ip: 'Detecting...' });
  const [brandingLaws, setBrandingLaws] = useState([]);
  const [reports, setReports] = useState([]);
  const [domainStatuses, setDomainStatuses] = useState({}); // { brandId: { com: 'available', in: 'taken' } }
  const [checkingDomains, setCheckingDomains] = useState(null);
  useEffect(() => {
    if (activeView === 'overview') {
      fetchBrands();
      fetchDomainStatuses();
    }
    if (activeView === 'entities') {
      fetchEntities();
    }
    if (activeView === 'logs') {
      fetchLogs();
    }
    if (activeView === 'laws') {
      fetchBrandingLaws();
    }
    if (activeView === 'reports') {
      fetchReports();
    }
    if (activeView === 'settings') {
      fetchSettings();
      fetchIpInfo();
    }
  }, [activeView]);

  const handleFileUpload = async (entityId, file) => {
    if (!file) return;
    setUploading(entityId);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_id', entityId);

    try {
      await axios.post(`${API_URL}?action=upload_attachment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('File attached successfully.');
      // Refresh meta-data or entity if needed
    } catch (err) {
      console.error("Upload failed", err);
      alert('Upload failed.');
    } finally {
      setUploading(null);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?action=get_activity`);
      setLogs(res.data);
    } catch (err) {
      console.error("Failed to fetch logs", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntities = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?action=get_entities&type=client`);
      setEntities(res.data);
    } catch (err) {
      console.error("Failed to fetch entities", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRounds = async (clientId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?action=get_entities&type=round&parent_id=${clientId}`);
      setRounds(res.data);
    } catch (err) {
      console.error("Failed to fetch rounds", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntity = async (type, title, parentId = 0) => {
    try {
      await axios.post(`${API_URL}?action=create_entity`, { type, title, parent_id: parentId });
      type === 'client' ? fetchEntities() : fetchRounds(parentId);
    } catch (err) {
      alert("Creation failed.");
    }
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?action=get_settings`);
      setSettings(res.data);
    } catch (err) {
      console.error("Failed to fetch settings", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (updatedSettings) => {
    setSavingSettings(true);
    try {
      await axios.post(`${API_URL}?action=save_settings`, { settings: updatedSettings });
      alert('Settings updated successfully.');
      fetchSettings();
    } catch (err) {
      console.error("Failed to save settings", err);
      alert('Failed to save settings.');
    } finally {
      setSavingSettings(false);
    }
  };

  const syncDatabase = async () => {
    if (!confirm("Are you sure you want to synchronize the database schema? This will ensure all online tables are up to date.")) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?action=setup_database`);
      alert(res.data.message);
    } catch (err) {
      alert("Sync failed.");
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?action=get_brands`);
      setBrandsData(res.data);
    } catch (err) {
      console.error("Failed to fetch brands", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandingLaws = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}?action=get_branding_laws`);
      setBrandingLaws(res.data);
      
      // Initialize scores for all laws if empty
      const initialScores = {};
      res.data.forEach(law => {
        initialScores[law.id] = { score: 5, reasoning: '' };
      });
      setAuditScores(initialScores);
    } catch (err) {
      console.error("Failed to fetch branding laws", err);
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateLegacy = async () => {
    if (!confirm("Start migration of legacy candidates from JSON to MySQL? This will not duplicate existing entries.")) return;
    setMigrating(true);
    try {
      const res = await axios.get(`${API_URL}?action=migrate_legacy_data`);
      alert(res.data.message);
      fetchBrands();
    } catch (err) {
      alert("Migration failed.");
    } finally {
      setMigrating(false);
    }
  };

  const fetchIpInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}?action=get_ip_info`);
      setIpInfo(res.data);
    } catch (err) {
      console.error("IP detection failed", err);
    }
  };
  const [savingAudit, setSavingAudit] = useState(false);

  const handleSaveAudit = async () => {
    if (!selectedAudit) return;
    setSavingAudit(true);
    try {
      for (const lawId in auditScores) {
        await axios.post(`${API_URL}?action=save_audit_result`, {
          candidate_id: selectedAudit.id || 1, // Mocking ID if not present in JSON
          law_id: lawId,
          score: auditScores[lawId].score,
          reasoning: auditScores[lawId].reasoning
        });
      }
      alert('Audit strategy saved to database.');
      setSelectedAudit(null);
    } catch (err) {
      console.error(err);
      alert('Failed to save audit.');
    } finally {
      setSavingAudit(false);
    }
  };
  
  const stats = [
    { label: 'Total Brands', value: brandsData?.length || 0, icon: FileText, color: 'text-brand' },
    { label: 'Client Rounds', value: '7', icon: TrendingUp, color: 'text-blue-400' },
    { label: 'Top Tier (5★)', value: (brandsData || []).filter(b => (b?.stars?.length || 0) >= 5).length, icon: ShieldCheck, color: 'text-green-400' },
    { label: 'Domains Checked', value: '1,200+', icon: Globe, color: 'text-purple-400' },
  ];

  const filteredBrands = (brandsData || []).filter(b => 
    (b?.name || "").toLowerCase().includes((searchTerm || "").toLowerCase()) ||
    (b?.source || "").toLowerCase().includes((searchTerm || "").toLowerCase())
  ).slice(0, 15);

  return (
    <div className="flex min-h-screen bg-industrial-900 text-white font-sans">
      {/* Sidebar */}
      <aside className="w-64 border-r border-industrial-800 flex flex-col p-6">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center text-industrial-900 font-bold">D</div>
          <span className="font-bold tracking-tight uppercase text-sm">Dexter Admin</span>
        </div>

        <nav className="flex-1 space-y-2">
          <NavItem icon={LayoutDashboard} label="Overview" active={activeView === 'overview'} onClick={() => setActiveView('overview')} />
           <NavItem icon={Users} label="Client Projects" active={activeView === 'entities'} onClick={() => setActiveView('entities')} />
           <NavItem icon={ShieldCheck} label="Branding Laws" active={activeView === 'laws'} onClick={() => setActiveView('laws')} />
           <NavItem icon={BookOpen} label="Reports Library" active={activeView === 'reports'} onClick={() => setActiveView('reports')} />
           <NavItem icon={Database} label="System Backup" onClick={() => window.open(`${API_URL}?action=export_db`, '_blank')} />
          <NavItem icon={Settings} label="Toolkit Settings" active={activeView === 'settings'} onClick={() => setActiveView('settings')} />
        </nav>

        <button 
          onClick={logout}
          className="mt-auto flex items-center gap-3 text-industrial-500 hover:text-red-400 transition-colors p-2"
        >
          <LogOut className="w-5 h-5" />
          <span>Sign Out</span>
        </button>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-y-auto">
        <header className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold">Administrative Hub</h1>
            <p className="text-industrial-400 text-sm">Welcome back, {user?.name}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-industrial-500" />
              <input 
                type="text" 
                placeholder="Search brands or rounds..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-industrial-800 border border-industrial-700 rounded-lg py-2 pl-10 pr-4 text-sm focus:ring-1 focus:ring-brand outline-none w-64"
              />
            </div>
            <button className="btn-primary py-2 px-4 text-sm flex items-center gap-2">
              <PlusCircle className="w-4 h-4" /> New Audit
            </button>
          </div>
        </header>

        {activeView === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
              {(stats || []).map((s, i) => (
                <div key={i} className="glass-card p-6 border-industrial-800">
                  <div className="flex items-center justify-between mb-2">
                    <s.icon className={`w-5 h-5 ${s.color}`} />
                    <span className="text-xs text-industrial-500 font-mono">LIVE</span>
                  </div>
                  <div className="text-2xl font-bold">{s.value}</div>
                  <div className="text-xs text-industrial-400 uppercase tracking-widest mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Recent Brands Table */}
            <div className="glass-card overflow-hidden border-industrial-800">
              <div className="px-6 py-4 border-b border-industrial-800 flex justify-between items-center">
                <h3 className="font-semibold">Cumulative Brand History</h3>
                <button className="text-xs text-brand hover:underline flex items-center gap-1">
                   Export as Editable PDF <ChevronRight className="w-3 h-3" />
                </button>
              </div>
              <table className="w-full text-left">
                <thead className="bg-industrial-800/50 text-industrial-400 text-xs uppercase tracking-widest">
                  <tr>
                    <th className="px-6 py-3 font-medium">Brand Name</th>
                    <th className="px-6 py-3 font-medium">Syllables</th>
                    <th className="px-6 py-3 font-medium">Priority</th>
                    <th className="px-6 py-3 font-medium">Domain (.com/.in)</th>
                    <th className="px-6 py-3 font-medium">Source Round</th>
                    <th className="px-6 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-industrial-800">
                  {filteredBrands.map((brand, i) => (
                    <tr key={i} className="hover:bg-white/5 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="font-bold text-white group-hover:text-brand transition-colors">{brand.name}</div>
                        <div className="text-[10px] text-industrial-500 uppercase mt-0.5 tracking-tighter">
                          {brand.display_name !== brand.name ? brand.display_name : 'Primary Key'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-0.5 bg-industrial-700 rounded text-xs">
                          {brand.syllables} Syl
                        </span>
                      </td>
                      <td className="px-6 py-4 text-brand tracking-widest">{brand.stars}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <DomainBadge 
                            tld="com" 
                            status={domainStatuses[brand.id]?.com} 
                            isChecking={checkingDomains === brand.id}
                            onClick={() => checkDomains(brand.name, brand.id)}
                          />
                          <DomainBadge 
                            tld="in" 
                            status={domainStatuses[brand.id]?.in} 
                            isChecking={checkingDomains === brand.id}
                            onClick={() => checkDomains(brand.name, brand.id)}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-industrial-400">Round {brand.source}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button 
                            onClick={async () => {
                              setAnalyzing(brand.name);
                              try {
                                const historySummary = (brandsData || []).slice(0, 10).map(b => `${b?.name} (${b?.stars} stars)`).join(', ');
                                const res = await axios.post(`${API_URL}?action=gemini_analyze`, { 
                                  brand_name: brand.name,
                                  project_context: "Dexter Communications branding for Kova × Rand York Joint Venture (Metallurgy/Structural Steel).",
                                  history_summary: historySummary
                                });
                                const aiText = res.data?.candidates?.[0]?.content?.parts?.[0]?.text || "Analysis failed.";
                                setAiResults(prev => ({ ...prev, [brand.name]: res.data }));
                                
                                // Also save to DB as a "system note" so the client can see it
                                await axios.post(`${API_URL}?action=save_feedback`, {
                                  brand_name: brand.name,
                                  client_username: 'SYSTEM_AI',
                                  comment: aiText,
                                  status: 'approved'
                                });
                              } catch (err) {
                                console.error(err);
                              } finally {
                                setAnalyzing(null);
                              }
                            }}
                            disabled={analyzing === brand.name}
                            className="text-industrial-500 hover:text-brand transition-colors p-1 rounded hover:bg-brand/10"
                            title="Run AI Analysis"
                          >
                            {analyzing === brand.name ? <RefreshCw className="w-4 h-4 animate-spin text-brand" /> : <Sparkles className="w-4 h-4" />}
                          </button>
                          <button 
                            onClick={() => setSelectedAudit(brand)}
                            className="text-industrial-500 hover:text-white transition-colors p-1"
                            title="Open Strategic Audit"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        </div>
                        {aiResults[brand.name] && (
                          <div className="absolute z-50 mt-2 w-64 p-4 glass-card text-[10px] shadow-2xl animate-fade-in border-brand/20">
                             <div className="font-bold text-brand uppercase mb-1">AI Insights</div>
                             <div className="text-slate-300 leading-tight">
                                {aiResults[brand.name]?.candidates?.[0]?.content?.parts?.[0]?.text || "Analysis failed."}
                             </div>
                             <button onClick={() => setAiResults(p => { delete p[brand.name]; return {...p} })} className="mt-2 text-industrial-500 hover:text-white underline">Close</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-6 py-3 bg-industrial-800/20 text-center">
                <button className="text-industrial-500 text-xs hover:text-brand transition-colors">
                  View All 345 Brand Entries
                </button>
              </div>
            </div>
          </>
        )}

        {activeView === 'entities' && (
          <div className="glass-card p-8 border-brand/10 min-h-[600px]">
            <header className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Users className="w-6 h-6 text-brand" /> 
                  {selectedClient ? `Project: ${selectedClient.title}` : "Master Entity Manager"}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <button 
                    onClick={() => { setSelectedClient(null); setSelectedRound(null); }}
                    className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${!selectedClient ? 'bg-brand text-black' : 'bg-industrial-800 text-industrial-400'}`}
                  >
                    Clients
                  </button>
                  {selectedClient && <ChevronRight className="w-3 h-3 text-industrial-600" />}
                  {selectedClient && (
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-brand text-black">
                      Rounds
                    </span>
                  )}
                </div>
              </div>
              
              {!selectedClient && (
                <button 
                  onClick={() => {
                    const title = prompt("Enter Client Name:");
                    if (title) handleCreateEntity('client', title);
                  }}
                  className="btn-primary py-2 px-4 text-xs flex items-center gap-2"
                >
                  <PlusCircle className="w-4 h-4" /> New Project
                </button>
              )}
              {selectedClient && !selectedRound && (
                 <button 
                  onClick={() => {
                    const title = prompt("Enter Round Version (e.g. 3.3.2):");
                    if (title) handleCreateEntity('round', title, selectedClient?.id);
                  }}
                  className="btn-primary py-2 px-4 text-xs flex items-center gap-2"
                >
                  <PlusCircle className="w-4 h-4" /> New Round
                </button>
              )}
            </header>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
               {loading ? (
                 <div className="col-span-3 flex items-center justify-center p-20">
                    <RefreshCw className="w-8 h-8 animate-spin text-brand" />
                 </div>
               ) : !selectedClient ? (
                 (entities || []).map((entity) => (
                   <div key={entity?.id || Math.random()} className="p-6 bg-brand/5 rounded-2xl border border-brand/20 hover:border-brand/50 transition-all flex flex-col justify-between">
                      <div>
                        <div className="text-xs font-mono text-brand mb-2">ENTITY: {entity?.slug?.toUpperCase() || entity?.id}</div>
                        <div className="text-lg font-bold">{entity?.title}</div>
                        <div className="text-xs text-industrial-400 mt-1 uppercase">
                           {entity?.child_count || 0} Active Rounds | {entity?.created_at ? new Date(entity.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </div>
                      <button 
                        onClick={() => { setSelectedClient(entity); fetchRounds(entity?.id); }}
                        className="mt-6 w-full py-2 bg-industrial-800 hover:bg-brand/20 border border-industrial-700 hover:border-brand/50 rounded-xl text-xs font-bold text-center transition-all"
                      >
                        Manage Project
                      </button>
                   </div>
                 ))
               ) : (
                 (rounds || []).map((round) => (
                    <div key={round?.id || Math.random()} className="p-6 bg-industrial-800/30 rounded-2xl border border-industrial-700 hover:border-brand/30 transition-all flex flex-col justify-between">
                      <div>
                        <div className="text-xs font-mono text-industrial-500 mb-2">ROUND VERSION: {round?.title}</div>
                        <div className="text-lg font-bold">Strategic Audit</div>
                        <div className="text-xs text-industrial-400 mt-1 uppercase">
                           {round?.child_count || 0} Candidates | Status: {round?.status}
                        </div>
                      </div>
                      <button 
                        onClick={() => { setSelectedRound(round); setActiveView('overview'); }}
                        className="mt-6 w-full py-2 bg-brand/10 hover:bg-brand/20 border border-brand/20 rounded-xl text-xs font-bold text-brand text-center transition-all"
                      >
                        Process Candidates
                      </button>
                    </div>
                 ))
               )}
            </div>
            
            {!loading && !selectedClient && (entities || []).length === 0 && (
               <div className="text-center py-20 text-industrial-500 italic text-sm">
                  No client projects found. Start by creating a new project.
               </div>
            )}
            {!loading && selectedClient && (rounds || []).length === 0 && (
               <div className="text-center py-20 text-industrial-500 italic text-sm">
                  No rounds found for this project. Start by creating v1.0.
               </div>
            )}
          </div>
        )}

        {activeView === 'laws' && (
          <div className="glass-card p-10 border-brand/10">
            <header className="mb-10 text-center">
              <div className="text-brand text-xs font-mono uppercase tracking-[0.4em] mb-2">Strategic Framework</div>
              <h2 className="text-4xl font-black mb-4">The 22 Immutable Laws of Branding</h2>
              <p className="text-industrial-400 max-w-2xl mx-auto italic text-sm">
                Engineering iconic identities through industry-standard principles, translated for strategic brand naming excellence.
              </p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(brandingLaws || []).map((law) => (
                <div key={law.id} className="p-6 bg-industrial-800/40 border border-industrial-800 rounded-2xl hover:border-brand/30 transition-all group">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand font-black text-sm group-hover:bg-brand group-hover:text-industrial-900 transition-colors">
                      {law.id}
                    </div>
                    <div>
                      <h4 className="font-bold text-white group-hover:text-brand transition-colors mb-2">{law.law_name}</h4>
                      <p className="text-xs text-industrial-400 leading-relaxed font-sans">{law.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeView === 'reports' && (
          <div className="glass-card p-8 border-brand/10">
            <header className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-brand" /> Reports & Strategy Library
                </h2>
                <p className="text-industrial-400 text-sm mt-1">Manage, import, and generate strategic branding reports.</p>
              </div>
              <div className="flex gap-3">
                <label className="btn-primary py-2 px-4 text-xs flex items-center gap-2 cursor-pointer">
                  <PlusCircle className="w-4 h-4" /> Import MD/JSON
                  <input type="file" className="hidden" accept=".md,.json" onChange={(e) => handleImportReport(e.target.files[0])} />
                </label>
              </div>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {loading ? (
                <div className="col-span-2 flex justify-center py-20">
                  <RefreshCw className="w-8 h-8 animate-spin text-brand" />
                </div>
              ) : (reports || []).map((report) => (
                <div key={report.id} className="p-6 bg-industrial-800/30 rounded-2xl border border-industrial-800 hover:border-brand/30 transition-all flex justify-between items-start group">
                  <div>
                    <div className="text-[10px] font-mono text-brand mb-1 uppercase tracking-widest">{report.report_type} | {report.format}</div>
                    <h4 className="font-bold text-lg mb-1">{report.title}</h4>
                    <p className="text-xs text-industrial-500">{new Date(report.created_at).toLocaleDateString()} • {report.content.length} characters</p>
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => {
                        const blob = new Blob([report.content], { type: 'text/markdown' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${report.title}.md`;
                        a.click();
                      }}
                      className="p-2 hover:bg-brand/10 rounded-lg text-industrial-400 hover:text-brand transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {!loading && (reports || []).length === 0 && (
              <div className="text-center py-20 text-industrial-500 italic text-sm">
                No reports saved in the library yet. Use the "Import" button to add strategic documents.
              </div>
            )}
          </div>
        )}

        {activeView === 'settings' && (
          <div className="glass-card p-8 border-brand/10">
            <header className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Settings className="w-6 h-6 text-brand" /> Toolkit Configuration
                </h2>
                <p className="text-industrial-400 text-sm mt-1">Manage global API credentials and security whitelisting.</p>
              </div>
              <button 
                onClick={syncDatabase}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-industrial-800 hover:bg-brand/20 border border-industrial-700 hover:border-brand/40 rounded-xl text-xs font-bold transition-all"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                System Sync
              </button>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
               <div className="space-y-6">
                  <div className="flex justify-between items-center border-b border-industrial-800 pb-2">
                    <h3 className="text-sm font-bold uppercase tracking-widest text-industrial-500">API Credentials</h3>
                    <div className="flex gap-4">
                      <div className="text-[10px] bg-industrial-800 px-2 py-1 rounded">
                        <span className="text-industrial-500 mr-1">SERVER IP:</span>
                        <span className="text-brand font-mono">{ipInfo.server_ip}</span>
                      </div>
                    </div>
                  </div>
                  {(settings || []).filter(s => s?.setting_key !== 'IP_WHITELIST').map(s => (
                    <div key={s.setting_key} className="space-y-2">
                      <label className="text-xs font-mono text-brand flex justify-between">
                        {s.setting_key}
                        <span className="text-industrial-500 font-sans italic">{s.description}</span>
                      </label>
                      <input 
                        type="password"
                        defaultValue={s.setting_value}
                        onBlur={(e) => handleSaveSettings({ [s.setting_key]: e.target.value })}
                        className="w-full bg-industrial-900 border border-industrial-800 rounded-lg p-3 text-sm focus:ring-1 focus:ring-brand outline-none"
                      />
                    </div>
                  ))}
               </div>

               <div className="space-y-6">
                  <div className="flex justify-between items-center border-b border-industrial-800 pb-2">
                    <h3 className="text-sm font-bold uppercase tracking-widest text-industrial-500">Security & Access</h3>
                    <div className="text-[10px] bg-industrial-800 px-2 py-1 rounded">
                      <span className="text-industrial-500 mr-1">YOUR IP:</span>
                      <span className="text-red-400 font-mono">{ipInfo.visitor_ip}</span>
                    </div>
                  </div>
                  {(settings || []).filter(s => s?.setting_key === 'IP_WHITELIST').map(s => (
                    <div key={s.setting_key} className="space-y-2">
                      <label className="text-xs font-mono text-red-400 flex justify-between">
                        {s.setting_key}
                        <span className="text-industrial-500 font-sans italic">{s.description}</span>
                      </label>
                      <textarea 
                        defaultValue={s.setting_value}
                        onBlur={(e) => handleSaveSettings({ [s.setting_key]: e.target.value })}
                        placeholder="e.g. 192.168.1.1, 203.0.113.5"
                        className="w-full bg-industrial-900 border border-industrial-800 rounded-lg p-3 text-sm h-32 focus:ring-1 focus:ring-brand outline-none font-mono"
                      />
                      <p className="text-[10px] text-industrial-500 italic">Caution: Adding your IP here will restrict dashboard access to only whitelisted addresses.</p>
                    </div>
                  ))}
               </div>
            </div>

            <div className="mt-12 p-6 bg-red-900/10 border border-red-900/20 rounded-2xl">
               <h3 className="text-sm font-bold text-red-400 uppercase tracking-widest mb-2">Legacy Data Management</h3>
               <p className="text-xs text-industrial-400 mb-4">Transfer historical brand candidates from the portable `brands_data.json` into the live MySQL database hub.</p>
               <button 
                  onClick={handleMigrateLegacy}
                  disabled={migrating}
                  className="flex items-center gap-2 px-6 py-3 bg-red-950/20 hover:bg-red-900/40 border border-red-900/30 rounded-xl text-xs font-bold text-red-400 transition-all"
               >
                  {migrating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                  {migrating ? 'Migrating Database...' : 'Execute Legacy Migration'}
               </button>
            </div>
          </div>
        )}

        {activeView === 'logs' && (
          <div className="glass-card p-8 border-industrial-800">
            <header className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FileText className="w-6 h-6 text-brand" /> System Activity Stream
                </h2>
                <p className="text-industrial-400 text-sm mt-1">Real-time audit log of all administrative and automated actions.</p>
              </div>
              <a 
                href={`${API_URL}?action=export_activity`} 
                download 
                className="btn-primary py-2 px-4 text-xs flex items-center gap-2"
              >
                <ChevronRight className="w-4 h-4 rotate-90" /> Download logs (.csv)
              </a>
            </header>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-industrial-800/50 text-industrial-400 text-[10px] uppercase tracking-widest border-b border-industrial-700">
                  <tr>
                    <th className="px-6 py-4 font-medium">Timestamp</th>
                    <th className="px-6 py-4 font-medium">Actor</th>
                    <th className="px-6 py-4 font-medium">Action</th>
                    <th className="px-6 py-4 font-medium">Target</th>
                    <th className="px-6 py-4 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-industrial-800">
                  { (logs || []).map((log) => (
                    <tr key={log?.id || Math.random()} className="hover:bg-white/5 transition-colors group">
                      <td className="px-6 py-4 text-xs font-mono text-industrial-400">
                        {log?.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-brand/10 text-brand text-[10px] font-bold rounded uppercase">
                          {log?.user_username || 'SYSTEM'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold">{log?.action_type}</td>
                      <td className="px-6 py-4 text-xs text-industrial-300">{log?.entity_type} {log?.entity_id}</td>
                      <td className="px-6 py-4 text-xs text-industrial-500 italic max-w-xs truncate" title={log?.details}>
                        {log?.details}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loading && (
                <div className="flex justify-center p-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-brand" />
                </div>
              )}
              {!loading && (logs || []).length === 0 && (
                <div className="text-center py-20 text-industrial-500 italic text-sm">
                  No activity logs recorded yet.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Audit Manager Modal */}
        {selectedAudit && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-industrial-950/80 backdrop-blur-sm animate-fade-in">
            <div className="w-full max-w-4xl glass-card max-h-[90vh] overflow-y-auto shadow-2xl border-brand/20 p-8">
              <header className="flex justify-between items-start mb-8 border-b border-industrial-800 pb-6">
                <div>
                  <div className="text-brand text-xs font-mono uppercase tracking-[0.3em] mb-1">Strategic Audit Manager</div>
                  <h2 className="text-3xl font-bold tracking-tight">{selectedAudit.name}</h2>
                  <p className="text-industrial-400 text-sm mt-1">{selectedAudit.meaning || "Analyzing brand resonance for metallurgy sector."}</p>
                </div>
                <button 
                  onClick={() => setSelectedAudit(null)}
                  className="text-industrial-500 hover:text-white transition-colors"
                >
                  <PlusCircle className="w-6 h-6 rotate-45" />
                </button>
              </header>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                {(brandingLaws || []).map((law) => (
                  <div key={law.id} className="space-y-4 p-5 bg-industrial-800/30 rounded-xl border border-industrial-800/50 hover:border-brand/20 transition-all">
                    <div className="flex justify-between items-center">
                      <h4 className="font-bold text-sm uppercase tracking-wider">{law.law_name}</h4>
                      <span className="text-brand font-mono text-xl">{auditScores[law.id]?.score || 0}/10</span>
                    </div>
                    <p className="text-[10px] text-industrial-500 italic leading-relaxed">{law.description}</p>
                    <input 
                      type="range" min="0" max="10" step="1"
                      value={auditScores[law.id]?.score || 0}
                      onChange={(e) => setAuditScores(prev => ({
                        ...prev,
                        [law.id]: { ...prev[law.id], score: parseInt(e.target.value) }
                      }))}
                      className="w-full accent-brand bg-industrial-900 rounded-lg cursor-pointer"
                    />
                    <textarea 
                      placeholder={`Enter reasoning for ${law.law_name.toLowerCase()}...`}
                      value={auditScores[law.id]?.reasoning || ''}
                      onChange={(e) => setAuditScores(prev => ({
                        ...prev,
                        [law.id]: { ...prev[law.id], reasoning: e.target.value }
                      }))}
                      className="w-full bg-industrial-900/50 border border-industrial-800 rounded-lg p-3 text-xs text-slate-300 focus:ring-1 focus:ring-brand outline-none h-20 resize-none"
                    />
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center bg-brand/5 p-6 rounded-2xl border border-brand/10">
                <div className="flex gap-4">
                  <div className="p-3 bg-brand/10 rounded-xl text-brand">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-xs uppercase font-mono tracking-widest text-industrial-400">Calculated Suitability</div>
                    <div className="text-2xl font-bold">
                      {(Object.values(auditScores || {}).reduce((a, b) => a + (b?.score || 0), 0) / (brandingLaws?.length || 22)).toFixed(1)} / 10
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 items-center">
                  <div className="relative group">
                    <input 
                      type="file" 
                      id="audit-file-upload" 
                      className="hidden" 
                      onChange={(e) => handleFileUpload(selectedAudit.id || 99, e.target.files[0])}
                    />
                    <label 
                      htmlFor="audit-file-upload"
                      className="flex items-center gap-2 px-4 py-3 bg-industrial-800 hover:bg-industrial-700 border border-industrial-700 rounded-xl text-xs font-bold cursor-pointer transition-all"
                    >
                      {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4 text-brand" />}
                      Attach Document
                    </label>
                  </div>
                  <button 
                    onClick={handleSaveAudit}
                    disabled={savingAudit}
                    className="btn-primary px-8 py-3 flex items-center gap-2 group"
                  >
                    {savingAudit ? <RefreshCw className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5 group-hover:scale-110 transition-transform" />}
                    Finalize & Sync Strategy
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const DomainBadge = ({ tld, status, isChecking, onClick }) => {
  const isAvailable = status === 'available';
  const isTaken = status === 'taken';
  const isError = status === 'error';

  return (
    <button 
      onClick={onClick}
      disabled={isChecking}
      className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-tighter flex items-center gap-1 transition-all border ${
        isAvailable ? 'bg-green-500/10 border-green-500/30 text-green-400' :
        isTaken ? 'bg-red-500/10 border-red-500/30 text-red-400' :
        isError ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400' :
        'bg-industrial-800 border-industrial-700 text-industrial-500'
      } hover:border-brand/50`}
    >
      {isChecking ? <RefreshCw className="w-2.5 h-2.5 animate-spin" /> : 
       isAvailable ? <CheckCircle2 className="w-2.5 h-2.5" /> : 
       isTaken ? <XCircle className="w-2.5 h-2.5" /> : 
       <AlertCircle className="w-2.5 h-2.5" />}
      .{tld}
    </button>
  );
};

const NavItem = ({ icon: Icon, label, active = false, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${active ? 'bg-brand text-industrial-900 font-bold shadow-lg shadow-brand/20' : 'text-industrial-400 hover:bg-white/5 hover:text-white'}`}
  >
    <Icon className="w-5 h-5" />
    <span className="text-sm">{label}</span>
  </button>
);

const LayoutDashboard = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>
);
